import {
    isContinuousScorerPassing,
    isRecord,
    parseNonEmptyString,
    parseSproutRunMetrics,
    SPROUT_AGENT,
    SPROUT_JUDGE_SCORER_ID,
} from '@fruitful/llm-service-shared';
import {
    type SproutHandoffTarget,
    type SproutRunMetrics,
} from '@fruitful/types';
import { type StorageThreadType } from '@mastra/core/memory';

import {
    getThreadScorerScore,
    getThreadScorersForAgent,
    type QAThreadScorers,
} from '../utils/qaThreadScorers';

export const HANDOFF_LABELS: Record<SproutHandoffTarget, string> = {
    guide: 'Guide',
    support: 'Support',
};

export interface QASimulationThreadMetadata {
    qaTestCaseId?: string;
    batchId?: string;
    promptName?: string;
    fixtureId?: string;
    metrics?: SproutRunMetrics | null;
    scorers?: QAThreadScorers | null;
}

export const isDefaultPromptName = (promptName?: string | null): boolean => {
    return promptName == null || promptName.trim() === '';
};

export const isCustomPromptName = (promptName?: string | null): boolean => {
    return !isDefaultPromptName(promptName);
};

export const toSimulationThreadMetadata = (
    value: unknown,
): QASimulationThreadMetadata | null => {
    if (!isRecord(value)) {
        return null;
    }

    return {
        qaTestCaseId: parseNonEmptyString(value.qaTestCaseId),
        batchId: parseNonEmptyString(value.batchId),
        promptName: parseNonEmptyString(value.promptName),
        fixtureId: parseNonEmptyString(value.fixtureId),
        metrics: parseSproutRunMetrics(value.metrics),
        scorers: getThreadScorersForAgent(value, SPROUT_AGENT.id),
    };
};

export interface QABatchSummary {
    batchId: string;
    promptName: string | null;
    ranAt: Date | null;
    testCount: number;
    passed: number;
    failed: number;
}

const toDate = (value: Date | string | undefined): Date | null => {
    if (value == null) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const groupThreadsByBatch = (
    threads: StorageThreadType[],
): Map<string, StorageThreadType[]> => {
    const byBatch = new Map<string, StorageThreadType[]>();

    for (const thread of threads) {
        const metadata = toSimulationThreadMetadata(thread.metadata);
        if (metadata?.batchId == null) {
            continue;
        }

        const existing = byBatch.get(metadata.batchId) ?? [];
        existing.push(thread);
        byBatch.set(metadata.batchId, existing);
    }

    return byBatch;
};

export const summarizeBatch = (
    batchId: string,
    threads: StorageThreadType[],
): QABatchSummary => {
    const testCaseIds = new Set<string>();
    let promptName: string | null = null;
    let ranAt: Date | null = null;
    let passed = 0;
    let failed = 0;

    for (const thread of threads) {
        const metadata = toSimulationThreadMetadata(thread.metadata);
        if (metadata?.qaTestCaseId != null) {
            testCaseIds.add(metadata.qaTestCaseId);
        }

        if (promptName == null && isCustomPromptName(metadata?.promptName)) {
            promptName = metadata?.promptName ?? null;
        }

        const createdAt = toDate(thread.createdAt);
        if (createdAt != null && (ranAt == null || createdAt < ranAt)) {
            ranAt = createdAt;
        }

        const score = getThreadScorerScore(
            metadata?.scorers,
            SPROUT_JUDGE_SCORER_ID,
        );
        if (score != null) {
            if (isContinuousScorerPassing(score)) {
                passed += 1;
            } else {
                failed += 1;
            }
        }
    }

    return {
        batchId,
        promptName,
        ranAt,
        testCount: testCaseIds.size,
        passed,
        failed,
    };
};

export const computeBatchSummaries = (
    threads: StorageThreadType[],
): QABatchSummary[] => {
    const summaries = Array.from(groupThreadsByBatch(threads).entries()).map(
        ([batchId, batchThreads]) => summarizeBatch(batchId, batchThreads),
    );

    return summaries.sort((a, b) => {
        const aTime = a.ranAt?.getTime() ?? 0;
        const bTime = b.ranAt?.getTime() ?? 0;
        return bTime - aTime;
    });
};
